import styled from 'styled-components';
import { v2Color } from '@theme/v2';

// Metric label row (was the inline style on the MetricLabel outer <span>).
export const MetricLabelRoot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: ${({ $nowrap, $isMobile }) => ($nowrap || !$isMobile ? 'nowrap' : 'normal')};
`;

// Metric label text (was the inline style on the MetricLabel inner <span>).
export const MetricLabelText = styled.span`
  font-size: 14px;
  color: rgb(112,112,112);
  white-space: ${({ $nowrap, $isMobile }) => ($nowrap || !$isMobile ? 'nowrap' : 'normal')};
`;

// Info tooltip trigger wrapper (was the inline style on the MetricLabel tooltip <span>).
export const MetricTooltipTrigger = styled.span`
  display: inline-grid;
  place-content: center;
  color: rgb(170,170,170);
  cursor: pointer;
`;

// Card shell (was the inline style on the CardShellDiv <div>).
export const CardShell = styled.div`
  display: grid;
  gap: 14px;
  padding: 16px 20px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  // Never let any inner content push the card past its column and add a horizontal
  // scroll to the whole dashboard page (mweb). Clip, don't scroll.
  max-width: 100%;
  min-width: 0;
  overflow-x: clip;
`;

// Card title row (was the inline style on the CardTitle <div>).
export const CardTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

// Card title text (was the inline style on the CardTitle <span>).
export const CardTitleText = styled.span`
  color: ${v2Color('#272B41', 'text')};
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
`;

// Primary green CTA (was the raw <button> GreenCta with inline styles).
export const GreenCtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 0 0 auto;
  // Always sit at the (inline) end of the row — on mobile the assistance chip may be absent,
  // and this keeps Start Posting on the right instead of falling to the left.
  margin-inline-start: auto;
  border-radius: 6px;
  padding: 0 1.2em;
  height: 36px;
  font-weight: 600;
  line-height: 1.5715;
  white-space: nowrap;
  background: ${v2Color('#006169')};
  color: #fff;
  border: 1px solid ${v2Color('#006169')};
  cursor: pointer;
`;

// Vertical divider between metric columns (was the inline-styled `divider` <span>).
export const Divider = styled.span`
  align-self: center;
  height: 40px;
  margin: 0;
  border-inline-start: 1px solid rgba(5,5,5,0.06);
`;

// Content row (was the `contentRowStyle` object applied to a <div>).
export const ContentRow = styled.div`
  display: flex;
  align-items: ${({ $isMobile }) => ($isMobile ? 'flex-start' : 'center')};
  justify-content: space-between;
  gap: ${({ $isMobile }) => ($isMobile ? 16 : 24)}px;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
`;

// Left cluster (was the `leftClusterStyle` object applied to a <div>).
export const LeftCluster = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ $isMobile }) => ($isMobile ? 16 : 28)}px;
  min-width: 0;
`;

// License banner icon wrapper (was the inline-styled <span>).
export const BannerIconWrap = styled.span`
  display: grid;
  place-content: center;
  flex: 0 0 auto;
`;

// Metric column with a minimum width guard (was the inline-styled <div> blocks).
export const MetricGroup = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
`;

// Metric column without a minimum width guard (was the inline-styled <div> blocks).
export const MetricColumn = styled.div`
  display: grid;
  gap: 2px;
`;

// Large metric value number (was the repeated inline-styled count <span>).
export const MetricValue = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: ${v2Color('#272B41', 'text')};
  line-height: 1.2;
`;

// Plain "not posted" label in the clear state (was the inline-styled <span>).
export const NotPostedLabel = styled.span`
  font-size: 14px;
  color: rgb(112,112,112);
  white-space: nowrap;
`;

// Clear-state value row holding the 0 and the animated tick (was the inline-styled <span>).
export const ClearValueRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 9px;
`;

// Animated tick ring (was the inline-styled <span>; keeps the adlTickRing keyframes).
export const TickRing = styled.span`
  position: relative;
  display: grid;
  place-content: center;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${v2Color('#E1F2F0')};
  animation: adlTickRing 0.5s cubic-bezier(0.34,1.4,0.64,1) both;
`;

// Animated tick halo (was the inline-styled <span>; keeps the adlTickHalo keyframes).
export const TickHalo = styled.span`
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid ${v2Color('#006169')};
  animation: adlTickHalo 1.4s ease-out 0.25s 2 both;
`;

// Blurred value row in the unverified state (was the inline-styled <span>).
export const BlurRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

// Blurred masked value (was the inline-styled <span>).
export const BlurredValue = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: ${v2Color('#272B41', 'text')};
  line-height: 1.2;
  filter: blur(7px);
  user-select: none;
`;

// Lock icon wrapper in the unverified state (was the inline-styled <span>).
export const LockIconWrap = styled.span`
  display: inline-grid;
  place-content: center;
`;

// "Two steps" column in the unverified state (was the inline-styled <span>).
export const StepsColumn = styled.span`
  display: grid;
  gap: 5px;
  min-width: 0;
  align-self: start;
`;

// "Two steps" heading (was the inline-styled <span>).
export const StepsHeading = styled.span`
  color: ${v2Color('#272B41', 'text')};
  font-size: 15px;
  font-weight: 700;
  line-height: 1.5;
  white-space: nowrap;
`;

// Row holding the two step chips (was the inline-styled <span>).
export const StepsRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// Active (first) step chip (was the inline-styled <span>).
export const StepItemActive = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${v2Color('#272B41', 'text')};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

// Active step number badge (was the inline-styled <span>).
export const StepBadgeActive = styled.span`
  display: inline-grid;
  place-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${v2Color('#006169')};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
`;

// Chevron separator wrapper between the step chips (was the inline-styled <span>).
export const ChevronWrap = styled.span`
  color: rgb(200,200,200);
`;

// Inactive (second) step chip (was the inline-styled <span>).
export const StepItemInactive = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: rgb(112,112,112);
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`;

// Inactive step number badge (was the inline-styled <span>).
export const StepBadgeInactive = styled.span`
  display: inline-grid;
  place-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #F1F3F7;
  color: rgb(130,130,130);
  font-size: 11px;
  font-weight: 700;
`;

// Right-hand action cluster (was the inline-styled <span>).
export const ActionCluster = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 16px;
  flex: 0 0 auto;
  flex-wrap: wrap;
  row-gap: 10px;
  width: ${({ $isMobile }) => ($isMobile ? '100%' : 'auto')};
  // On mobile the cluster is full-width: LTR spreads assistance/CTA apart, while in
  // Arabic (RTL) we align the CTA to the (physical) left as requested — flex-end maps
  // to the left when the row direction is right-to-left.
  justify-content: ${({ $isMobile, $rtl }) => ($isMobile ? ($rtl ? 'flex-end' : 'space-between') : 'flex-end')};
`;
